<template>
  <div class="home-page">
    <LoaderOverlay :show-overlay="showSpinnerforCreateAcctFlow" />
    <Notification
      v-if="unifiedLoyaltyGlobalError"
      severity="error"
      aria-role="alert"
      :closable="false"
    >
      {{ unifiedLoyaltyGlobalError }}
    </Notification>
    <component
      :is="componentToRender"
      v-else-if="componentToRender"
    />
    <PreLoader v-else />
    <div class="slideout-overlay">
      <Slideout
        :id="slideout.slideout.id"
        :title="slideout.slideout.title"
        :disable-teleport="true"
      >
        <component
          :is="slideout.slideout.component"
          v-if="slideout.slideout.component"
          v-bind="slideout.slideout.props"
        />
      </Slideout>
    </div>
    <div
      v-if="modalOverlay.modal.id"
      class="modal-overlay"
    >
      <ModalOverlay
        :id="modalOverlay.modal.id"
        :title="modalOverlay.modal.title"
        :width="modalOverlay.modal.width"
        :disable-teleport="true"
        @close="onTwoStepVerificationModalClose"
      >
        <template #body>
          <component
            :is="modalOverlay.modal.component"
            v-if="modalOverlay.modal.component"
            v-bind="modalOverlay.modal.props"
          />
        </template>
      </ModalOverlay>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import LoaderOverlay from '@nuxt-common/atomic-ui/app/components/loaderOverlay/LoaderOverlay.vue';
import ModalOverlay from '@nuxt-common/atomic-ui/app/components/modalOverlay/ModalOverlay.vue';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import Slideout from '@nuxt-common/atomic-ui/app/components/slideout/Slideout.vue';
import { ASSOCIATION_PAGE, brand } from 'config';
import { getSummaryHeaders } from '../utils/getHeader';
import { isValidLoyaltyHubSection, normalizeLoyaltyHubSection, scrollToLoyaltyHubSection } from '../utils/loyaltyHubSectionScrollUtil';
import { loadOverlayComponent } from '../utils/loadOverlayComponent';
import { restoreBodyScroll } from '../utils/restoreBodyScroll';

import AccountSummary from '../components/AccountSummary';
import Association from '../components/Association';
import PreLoader from '../components/PreLoader';
import { useLoyaltyHubStore } from '../stores/useLoyaltyHubStore.js';
import { useModalOverlayStore } from '../stores/useModalOverlayStore';
import { useSlideoutStore } from '../stores/useSlideoutStore';

defineOptions({ name: 'LoyaltyHome' });

const route = useRoute();
const loyaltyHub = useLoyaltyHubStore();
const modalOverlay = useModalOverlayStore();
const slideout = useSlideoutStore();
const starRewardsWithoutCC = computed(() => loyaltyHub.starRewardsWithoutCC);
const loyallistEnrolled = computed(() => loyaltyHub.loyallistEnrolled);
const accountAssociationResponse = computed(() => loyaltyHub.accountAssociationResponse);
const otpVerification = computed(() => loyaltyHub.otpVerification);
const unifiedLoyaltyGlobalError = computed(() => loyaltyHub.unifiedLoyaltyGlobalError);
const isLoyallistEnrollSubmitted = computed(() => loyaltyHub.isLoyallistEnrollSubmitted);
const showSpinnerforCreateAcctFlow = computed(() => loyaltyHub.showSpinnerforCreateAcctFlow);

// getters
const getUserAssociation = computed(() => loyaltyHub.userAssociation);
const unifiedLoyalty = computed(() => loyaltyHub.unifiedLoyalty);

// action
const fetchSummary = async () =>
  loyaltyHub.fetchUnifiedLoyaltySummary({
    headers: await getSummaryHeaders(),
  });

const setSlideoutDetails = async (val) => {
  slideout.setSlideout(val);
};

const componentToRender = computed(() => {
  if (getUserAssociation.value !== undefined) {
    return getUserAssociation.value ? AccountSummary : Association;
  }
  return null;
});

const scrollToSectionFromRoute = async () => {
  const section = normalizeLoyaltyHubSection(route.query?.section);

  if (componentToRender.value !== AccountSummary || !getUserAssociation.value || !unifiedLoyalty.value || !isValidLoyaltyHubSection(section, brand)) {
    return;
  }

  await nextTick();
  await nextTick();
  await scrollToLoyaltyHubSection(section, {
    maxAttempts: 50,
    intervalMs: 100,
    followUpDelays: [300, 800, 1500],
  });
};

watch([() => route.query?.section, unifiedLoyalty, getUserAssociation, componentToRender], () => scrollToSectionFromRoute(), {
  flush: 'post',
  immediate: true,
});

const closePriorLinkingOverlays = () => {
  slideout.close('manage-membership-slideout');
  slideout.close('association-join-now-slideout');
  if (modalOverlay.showModal['otpVerifyOverlay']) {
    modalOverlay.close('otpVerifyOverlay');
  }
  if (modalOverlay.showModal['emailVerifyOverlay']) {
    modalOverlay.close('emailVerifyOverlay');
  }
  restoreBodyScroll();
};

const TWO_STEP_VERIFICATION_MODAL_IDS = new Set(['emailVerifyOverlay', 'otpVerifyOverlay']);
const USER_DISMISS_SOURCES = new Set(['backdrop', 'button', 'esc']);

const onTwoStepVerificationModalClose = async (id, source) => {
  if (!TWO_STEP_VERIFICATION_MODAL_IDS.has(id) || !USER_DISMISS_SOURCES.has(source)) {
    return;
  }

  await nextTick();

  const emailOpen = !!modalOverlay.showModal.emailVerifyOverlay;
  const otpOpen = !!modalOverlay.showModal.otpVerifyOverlay;
  if (!emailOpen && !otpOpen) {
    restoreBodyScroll();
  }
};

const triggerSlideout = async () => {
  const component = await loadOverlayComponent('linkSuccessNotification');
  closePriorLinkingOverlays();

  setSlideoutDetails({
    id: 'success-notification-slideout',
    title: isLoyallistEnrollSubmitted.value ? ASSOCIATION_PAGE.JOIN_LOYALLIST : ASSOCIATION_PAGE.MANAGE_MEMBERSHIP,
    component,
  });
  await nextTick();
  slideout.open('success-notification-slideout');
};

watch([starRewardsWithoutCC, otpVerification, accountAssociationResponse], async (newValue, oldValue) => {
  const [sr, otp, acct] = newValue;
  const prev = oldValue ?? [null, null, null];
  const objectPayload = (v) => v != null && typeof v === 'object';
  const starAdvanced = prev[0] == null && objectPayload(sr);
  const acctAdvanced = prev[2] == null && objectPayload(acct);
  const otpAdvanced = prev[1] == null && (otp === true || objectPayload(otp));
  if (!starAdvanced && !acctAdvanced && !otpAdvanced) {
    return;
  }
  await fetchSummary();
  await triggerSlideout();
});

watch(
  () => loyallistEnrolled.value,
  async (newValue) => {
    if (newValue !== null) {
      await triggerSlideout();
    }
  },
);

watch(
  () => slideout.expanded['success-notification-slideout'],
  async (newValue, oldValue) => {
    if (oldValue === true && newValue === false) {
      restoreBodyScroll();
      if (loyallistEnrolled.value !== null) {
        await fetchSummary();
      }
    }
  },
);

onMounted(async () => {
  await fetchSummary();
  const loyaltyRedirectData = window.sessionStorage.getItem('loyaltyUserData');
  const fromCreateAccount = document.referrer?.includes('createaccount');
  if (route?.query?.from === 'wallet') {
    await triggerSlideout();
  } else if (loyaltyRedirectData && fromCreateAccount) {
    // Auto membership management only happens for the create-account auto-enroll flow.
    loyaltyHub.showSpinnerforCreateAcctFlow = true;
    try {
      await loyaltyHub.autoManageLoyaltyMembership();
    } finally {
      loyaltyHub.showSpinnerforCreateAcctFlow = false;
    }
  }
});
</script>
